<script setup lang="ts">
import { computed, h, onBeforeUnmount, onMounted, ref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { api } from '@/api'
import type FreeContent from '@/api/resources/free_content/model'
import { describeLineChange, normalizeNewlines, spliceEdit } from '@/utils/text-edit'

/**
 * The key in the editor, and its value on the server as of opening or the last
 * save — `null` while a new key is not on the server yet.
 */
interface OpenKey {
  name: string
  stored: string | null
}

const contents = ref<FreeContent[]>([])
const loading = ref(false)
const search = ref('')
const opened = ref<OpenKey | null>(null)
const draft = ref('')
const saving = ref(false)
// A key is on its way into the editor: until it lands, the field still holds the
// previous key's text, which must not be saved or edited under either name.
const opening = ref(false)
const editor = ref<HTMLElement>()
// The width below which the list and the editor stack; matches .layout below.
const stacked = window.matchMedia('(max-width: 991px)')

// What «Сохранить» sends: the draft put back into the stored text, so that line
// breaks the textarea rewrote are not saved rewritten.
const toSave = computed(() =>
  opened.value ? spliceEdit(opened.value.stored ?? '', draft.value) : '',
)
const dirty = computed(() => {
  const key = opened.value
  if (!key) return false
  return key.stored === null ? draft.value !== '' : toSave.value !== key.stored
})

const rows = computed(() => {
  const query = search.value.trim().toLowerCase()
  return contents.value
    .filter(
      (c) => !query || c.name.toLowerCase().includes(query) || c.body.toLowerCase().includes(query),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
})

const firstLine = (body: string) =>
  body.split(/\r\n|\r|\n/).find((line) => line.trim() !== '') ?? ''

const rowClass = ({ row }: { row: FreeContent }) =>
  row.name === opened.value?.name ? 'is-open' : ''

/** Every key, read afresh; `null` if that failed — the http client has said why. */
const load = async (): Promise<FreeContent[] | null> => {
  loading.value = true
  try {
    contents.value = await api.freeContent.list()
    return contents.value
  } catch {
    return null
  } finally {
    loading.value = false
  }
}

/** True when there is nothing unsaved to lose, or the operator agrees to lose it. */
const confirmDiscard = (
  message = `В «${opened.value?.name}» есть несохранённые правки. Уйти без сохранения?`,
  confirmButtonText = 'Уйти без сохранения',
) => {
  if (!dirty.value) return Promise.resolve(true)
  return ElMessageBox.confirm(message, 'Несохранённые правки', {
    confirmButtonText,
    cancelButtonText: 'Остаться',
    type: 'warning',
  }).then(
    () => true,
    () => false,
  )
}

let latestShow = 0
/** Opens `name` in the editor with its value as the server has it right now. */
const show = async (name: string) => {
  const ticket = ++latestShow
  opening.value = true
  try {
    const fresh = await load()
    if (!fresh || ticket !== latestShow) return
    const row = fresh.find((c) => c.name === name)
    opened.value = { name, stored: row ? row.body : null }
    draft.value = normalizeNewlines(row?.body ?? '')
    // On a narrow screen the editor sits below the list. Scroll in the next frame,
    // once the field has grown to its text: before that the page may be too short
    // to scroll at all.
    if (stacked.matches)
      requestAnimationFrame(() => editor.value?.scrollIntoView({ behavior: 'smooth' }))
  } finally {
    if (ticket === latestShow) opening.value = false
  }
}

const openKey = async (name: string) => {
  if (saving.value || opened.value?.name === name) return
  if (await confirmDiscard()) await show(name)
}

const addKey = async () => {
  if (saving.value || !(await confirmDiscard())) return
  const answer = await ElMessageBox.prompt(
    'Бот находит значение по точному имени ключа.',
    'Новый ключ',
    {
      confirmButtonText: 'Дальше',
      cancelButtonText: 'Отмена',
      inputValue: '',
      inputPlaceholder: 'например, conditions_text_ru',
      inputValidator: (value) =>
        value.trim() === ''
          ? 'Введите имя ключа'
          : value !== value.trim()
            ? 'Без пробелов в начале и в конце'
            : true,
    },
  ).catch(() => null)
  if (!answer) return
  const name = answer.value
  await show(name)
  // Typing the name of a key that exists used to replace its value blind.
  if (opened.value?.name === name && opened.value.stored !== null) {
    ElMessage.info(`Ключ «${name}» уже есть — он открыт с текущим значением`)
  }
}

const revert = async () => {
  const key = opened.value
  if (!key || saving.value || opening.value) return
  const agreed = await confirmDiscard(
    `Отбросить правки в «${key.name}» и вернуть значение, которое сейчас на сервере?`,
    'Отбросить правки',
  )
  if (agreed) await show(key.name)
}

const save = async () => {
  const key = opened.value
  if (!key || !dirty.value || saving.value || opening.value) return
  const value = toSave.value
  const isNew = key.stored === null

  const lines = [
    isNew
      ? `Создать ключ «${key.name}»?`
      : `Сохранить «${key.name}»? ${describeLineChange(
          normalizeNewlines(key.stored ?? ''),
          normalizeNewlines(value),
        )}.`,
  ]
  if (value === '') lines.push('Значение пустое.')
  const confirmed = await ElMessageBox.confirm(
    h(
      'div',
      lines.map((line) => h('p', line)),
    ),
    isNew ? 'Новый ключ' : 'Сохранить изменения?',
    {
      confirmButtonText: isNew ? 'Создать' : 'Сохранить',
      cancelButtonText: 'Отмена',
      type: 'info',
    },
  ).then(
    () => true,
    () => false,
  )
  if (!confirmed) return

  saving.value = true
  try {
    // Somebody may have changed the key since it was opened: say so rather than
    // overwrite their text without a word.
    const current = await load()
    if (!current) return
    const now = current.find((c) => c.name === key.name)?.body ?? null
    if (now !== key.stored) {
      const overwrite = await ElMessageBox.confirm(
        `Пока «${key.name}» был открыт, его значение на сервере изменилось. Если сохранить, те изменения пропадут.`,
        'Значение изменилось',
        { confirmButtonText: 'Всё равно сохранить', cancelButtonText: 'Отмена', type: 'warning' },
      ).then(
        () => true,
        () => false,
      )
      if (!overwrite) return
    }

    try {
      await api.freeContent.create({ name: key.name, body: value })
    } catch {
      return // the http client has shown the error; the edits stay in the editor
    }

    // Read it back: what the bot reads from now on must be exactly what was sent.
    const after = await load()
    const readBack = after?.find((c) => c.name === key.name)?.body
    opened.value = { name: key.name, stored: readBack ?? value }
    if (readBack === value) {
      ElMessage.success(`«${key.name}» сохранён и прочитан обратно — совпадает символ в символ`)
    } else if (readBack === undefined) {
      ElMessage.warning(`«${key.name}» сохранён, но прочитать его обратно не удалось`)
    } else {
      ElMessageBox.alert(
        'Сервер вернул не то, что было отправлено. Расхождение осталось в редакторе несохранёнными правками.',
        'Сохранено с расхождением',
        { type: 'error' },
      )
    }
  } finally {
    saving.value = false
  }
}

onBeforeRouteLeave(() => confirmDiscard())

const warnOnUnload = (event: BeforeUnloadEvent) => {
  if (!dirty.value) return
  event.preventDefault()
  event.returnValue = true // Chrome and Edge before 119 prompt only when this is set
}
onMounted(() => window.addEventListener('beforeunload', warnOnUnload))
onBeforeUnmount(() => window.removeEventListener('beforeunload', warnOnUnload))

load()
</script>

<template>
  <main>
    <h2>Контент</h2>
    <p class="hint">
      Значения free_content, которые бот берёт как есть. Сохраняется ровно то, что в поле: переносы,
      пустые строки, отступы и эмодзи.
    </p>

    <div class="layout">
      <section>
        <div class="list-tools">
          <el-input v-model="search" placeholder="Поиск по ключу и тексту" clearable />
          <el-button :disabled="saving" @click="addKey">Новый ключ</el-button>
        </div>
        <el-table
          v-loading="loading"
          class="keys"
          :data="rows"
          :row-class-name="rowClass"
          empty-text="Ключей нет"
          @row-click="(row: FreeContent) => openKey(row.name)"
        >
          <el-table-column label="Ключ">
            <template #default="{ row }">
              <div class="key-name">{{ row.name }}</div>
              <div class="key-preview">{{ firstLine(row.body) || '(пусто)' }}</div>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section ref="editor" class="editor-pane">
        <el-card v-if="opened" class="editor" shadow="never">
          <div class="editor-tools">
            <span class="key-name">{{ opened.name }}</span>
            <el-tag v-if="opened.stored === null" type="info" size="small">новый</el-tag>
            <el-tag v-if="dirty" type="warning" size="small">не сохранено</el-tag>
            <span class="grow" />
            <el-button :disabled="!dirty || saving || opening" @click="revert">
              Отменить правки
            </el-button>
            <el-button type="primary" :disabled="!dirty || opening" :loading="saving" @click="save">
              Сохранить
            </el-button>
          </div>
          <el-input
            v-model="draft"
            type="textarea"
            :autosize="{ minRows: 4 }"
            :readonly="opening"
            placeholder="Значение ключа"
          />
        </el-card>
        <el-empty v-else description="Откройте ключ из списка или создайте новый" />
      </section>
    </div>
  </main>
</template>

<style scoped>
.hint {
  color: #64748b;
  font-size: 0.85rem;
}

/* A grid rather than el-row: el-row's gutter is negative margins, which push the
   page 10px wider than a phone screen. */
.layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 5fr);
  gap: 20px;
  align-items: start;
}

@media (max-width: 991px) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Clear of the sticky navbar when scrolled to on a narrow screen. */
.editor-pane {
  scroll-margin-top: 60px;
}

.list-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.keys :deep(.el-table__row) {
  cursor: pointer;
}

.keys :deep(.el-table__row.is-open > td.el-table__cell) {
  background-color: var(--el-color-primary-light-9);
}

.key-name {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 600;
  word-break: break-all;
}

.key-preview {
  color: #64748b;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* el-card hides its overflow, which would stop the toolbar from sticking. */
.editor {
  overflow: visible;
}

/* Long texts make the field taller than the screen: keep «Сохранить» in reach. */
.editor-tools {
  position: sticky;
  top: 70px;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  margin-bottom: 8px;
  background-color: var(--el-card-bg-color);
}

.grow {
  flex: 1;
}

@media (max-width: 768px) {
  .editor-tools {
    top: 50px;
  }
}
</style>
